// ----- Phase E-polish-extract-2 — shared PageToast component ----- //
//
// Extracted from inline copies in 6 admin pages (UsersPage,
// SettingsPage, SpecFilesPage, FormTemplatesPage, PromptLibraryPage,
// CompanyProfilePage). ValidationRulesPage is read-only and has no
// toast. The pre-extraction copies split into two style families:
//
//   - "Bootstrap" palette (UsersPage, FormTemplatesPage,
//     PromptLibraryPage, CompanyProfilePage) — older `#d4edda` /
//     `#155724` / `#f8d7da` / `#721c24` colors, 380px maxWidth,
//     no fontWeight, 16px close button, lighter boxShadow.
//
//   - "Modern Tailwind" palette (SpecFilesPage, SettingsPage) —
//     newer `#DCFCE7` / `#15803D` / `#FEE2E2` / `#B91C1C` colors,
//     480px maxWidth, fontWeight 600, 18px close button, heavier
//     boxShadow.
//
// Both families used identical prop shape ({ toast, onDismiss }),
// `role="status"`, `aria-live="polite"`, `data-testid="page-toast"`.
// None had auto-dismiss — all required clicking the × button.
//
// Reconciliation: the Modern Tailwind palette wins (more recent
// codebase direction, slightly more accessible contrast). The 4
// older pages get a subtle visual upgrade (brighter colors, bolder
// text, slightly larger size). The prop shape, role, testid, and
// no-auto-dismiss default are preserved exactly so existing tests
// and call sites continue to work without modification.
//
// `dismissAfterMs` is exposed as an opt-in for future callers but
// defaults to 0 (no auto-dismiss) to preserve current behavior.

import { useEffect, type ReactElement } from 'react';

export interface ToastMessage {
  readonly type: 'success' | 'error';
  readonly text: string;
  /**
   * Optional one-click follow-up (e.g. "Undo"). A toast with an action is not
   * auto-dismissed by useUiToast, so the action cannot vanish under the user.
   */
  readonly action?: { readonly label: string; readonly onClick: () => void };
}

export interface PageToastProps {
  readonly toast: ToastMessage | null;
  readonly onDismiss: () => void;
  /**
   * Auto-dismiss timeout in milliseconds.
   *
   * Default `0` — no auto-dismiss; user must click the × button.
   * This matches the pre-extraction inline behavior in all 6 admin
   * pages. Pass a non-zero value (e.g. 5000) to opt in to
   * timer-driven dismissal.
   */
  readonly dismissAfterMs?: number;
}

export function PageToast({
  toast,
  onDismiss,
  dismissAfterMs = 0,
}: PageToastProps): ReactElement | null {
  useEffect(() => {
    if (!toast || dismissAfterMs <= 0) return;
    const id = globalThis.setTimeout(onDismiss, dismissAfterMs);
    return () => {
      globalThis.clearTimeout(id);
    };
  }, [toast, dismissAfterMs, onDismiss]);

  if (!toast) return null;

  const isSuccess = toast.type === 'success';
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="page-toast"
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 400,
        padding: '10px 14px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 600,
        background: isSuccess ? 'var(--green-bg, #DCFCE7)' : 'var(--red-bg, #FEE2E2)',
        color: isSuccess ? 'var(--green, #15803D)' : 'var(--red, #B91C1C)',
        border: `1px solid ${isSuccess ? 'var(--green, #15803D)' : 'var(--red, #B91C1C)'}`,
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        maxWidth: 480,
      }}
    >
      <span>{toast.text}</span>
      {toast.action && (
        <button
          type="button"
          onClick={toast.action.onClick}
          data-testid="page-toast-action"
          style={{
            background: 'none',
            border: '1px solid currentColor',
            borderRadius: 4,
            cursor: 'pointer',
            color: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            padding: '2px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit',
          fontSize: 18,
          lineHeight: 1,
          padding: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
